import type {
  Entity,
  Filters,
  FilterByID,
  MetricsType,
  J2FiltersOptions,
  J2SelectedFilters,
  SelectedWorkflowFilterColumnsData,
  CurrentWorkflowAndNodeBreadCrumbData,
  SavedDashboardFiltersList,
  SavedDashboardFiltersListData,
  SelectedDashboardEntityFilters,
} from 'src/types/analytics-genesis';
import type {LineSeriesItem} from '@spaced-out/ui-design-system/lib/components/Charts';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import type {AnalyticsWorkflowsOverviewActions} from 'src/action-creators/analytics/analytics-workflows-overview';

import {
  RECEIVE_FILTERS_OPTIONS,
  RECEIVE_WORKFLOW_OPTIONS,
  RECEIVE_WORKFLOW_PLOT_DATA,
  RECEIVE_SAVED_DASHBOARDS,
  SET_CHART_FREQUENCY,
  SET_SELECTED_FILTERS,
  SET_SELECTED_ENTITIES,
  SET_SELECTED_WORKFLOWS,
  SET_SELECTED_METRICS_TYPE,
  SET_SELECTED_DATE_RANGE_AND_FREQUENCY,
  SET_SELECTED_DATES,
  CLEAR_SELECTED_FILTERS,
  RECEIVE_WORKFLOW_TABLE_COLUMNS,
  RECEIVE_NODE_TABLE_COLUMNS,
  RECEIVE_PEOPLE_TABLE_COLUMNS,
  SET_CURRENT_WORKFLOW_AND_NODE_DATA,
  SET_CURRENT_DASHBOARD,
  SET_LOADING_DASHBOARD_FILTERS,
  SET_SELECTED_DASHBOARD_ENTITY_FILTERS,
  SET_NAVIGATION_DOWN,
  SET_NAVIGATION_UP,
  SET_ALL_WORKFLOW_ENTITIES,
  SET_ALL_NODE_ENTITIES,
  SET_ALL_PEOPLE_ENTITIES,
  SET_ALL_WORKFLOW_FILTERS,
  SET_ALL_NODE_FILTERS,
  SET_ALL_PEOPLE_FILTERS,
} from 'src/action-creators/analytics/analytics-workflows-overview';


export type AnalyticsWorkflowsOverviewState = {
  filtersOptions: J2FiltersOptions;
  workflowPlotData: Array<LineSeriesItem> | null;
  selectedFilters: {
    [key: string]: J2SelectedFilters;
  };
  selectedEntities: {
    [key: string]: Array<Entity>;
  };
  selectedWorkflows: {
    [key: string]: Array<FilterByID>;
  };
  selectedMetricsType: MetricsType;
  selectedDateRange: DateRangeWithTimezone;
  selectedDates: {
    [key: string]: DateRangeWithTimezone;
  };
  chartFrequency: string;
  workflowTableColumnFilter: SelectedWorkflowFilterColumnsData | null;
  nodeTableColumnFilter: SelectedWorkflowFilterColumnsData | null;
  peopleTableColumnFilter: SelectedWorkflowFilterColumnsData | null;
  currentWorkflowAndNodeData: CurrentWorkflowAndNodeBreadCrumbData | null;
  saveDashboardFilterList: {
    [key: string]: SavedDashboardFiltersListData;
  };
  currentDashboard: {
    [key: string]: SavedDashboardFiltersList | null;
  };
  isDashboardFiltersLoading: boolean;
  selectedDashboardEntityFilters: {
    [key: string]: SelectedDashboardEntityFilters;
  };
  isNavigationDown: boolean | null;
  isNavigationUp: boolean | null;
  selectedAllWorkflowEntities: {
    [key: string]: Array<Entity>;
  };
  selectedAllPeopleEntities: {
    [key: string]: Array<Entity>;
  };
  selectedAllNodeEntities: {
    [key: string]: Array<Entity>;
  };
  selectedAllWorkflowFilters: {
    [key: string]: J2SelectedFilters;
  };
  selectedAllNodeFilters: {
    [key: string]: J2SelectedFilters;
  };
  selectedAllPeopleFilters: {
    [key: string]: J2SelectedFilters;
  };
};

const initialState: AnalyticsWorkflowsOverviewState = {
  filtersOptions: {
    workflow_status: [],
    recipient_type: [],
    node_type: [],
    workflow_name: [],
    workflow_category: [],
  },
  workflowPlotData: null,
  selectedDashboardEntityFilters: {},
  selectedFilters: {},
  selectedEntities: {},
  selectedWorkflows: {},
  selectedMetricsType: 'overview',
  selectedDateRange: {
    startDateUTC: null,
    startDate: null,
    endDateUTC: null,
    endDate: null,
  },
  currentWorkflowAndNodeData: null,
  selectedDates: {},
  chartFrequency: 'weekly',
  workflowTableColumnFilter: null,
  nodeTableColumnFilter: null,
  peopleTableColumnFilter: null,
  saveDashboardFilterList: {},
  currentDashboard: {},
  isDashboardFiltersLoading: true,
  isNavigationDown: null,
  isNavigationUp: null,
  selectedAllWorkflowEntities: {},
  selectedAllPeopleEntities: {},
  selectedAllNodeEntities: {},
  selectedAllWorkflowFilters: {},
  selectedAllNodeFilters: {},
  selectedAllPeopleFilters: {},
};

export default (
  state: AnalyticsWorkflowsOverviewState = initialState,
  action: AnalyticsWorkflowsOverviewActions,
): AnalyticsWorkflowsOverviewState => {
  switch (action.type) {
    case SET_CHART_FREQUENCY: {
      return {...state, chartFrequency: action.payload};
    }
    case SET_SELECTED_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedFilters: {
          ...state.selectedFilters,
          [dashboardKey]: filters,
        },
      };
    }
    case SET_SELECTED_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedEntities: {
          ...state.selectedEntities,
          [dashboardKey]: entities,
        },
      };
    }
    case SET_SELECTED_WORKFLOWS: {
      const {dashboardKey, workflows} = action.payload;
      return {
        ...state,
        selectedWorkflows: {
          ...state.selectedWorkflows,
          [dashboardKey]: workflows,
        },
      };
    }
    case SET_SELECTED_METRICS_TYPE: {
      return {...state, selectedMetricsType: action.payload};
    }
    case SET_SELECTED_DATE_RANGE_AND_FREQUENCY: {
      const {chartFrequency, dashboardKey, ...dateRange} = action.payload;
      return {
        ...state,
        selectedDateRange: dateRange,
        chartFrequency,
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: dateRange,
        },
      };
    }
    case RECEIVE_FILTERS_OPTIONS: {
      const {filtersOptions} = state;
      return {
        ...state,
        filtersOptions: {
          ...filtersOptions,
          ...action.payload,
        },
        selectedDateRange: {
          startDateUTC: null,
          startDate: null,
          endDateUTC: null,
          endDate: null,
        },
        selectedMetricsType: 'overview',
      };
    }
    case RECEIVE_WORKFLOW_OPTIONS: {
      const {filtersOptions} = state;
      return {
        ...state,
        filtersOptions: {
          ...filtersOptions,
          workflow_name: action.payload,
        },
      };
    }
    case RECEIVE_WORKFLOW_PLOT_DATA: {
      return {...state, workflowPlotData: action.payload};
    }
    case CLEAR_SELECTED_FILTERS: {
      const {dashboardKey} = action.payload;
      return {
        ...state,
        selectedFilters: {
          ...state.selectedFilters,
          [dashboardKey]: {
            workflow_categories: [],
            entity_data_filter: [],
            workflow_status: [],
            recipient_type: [],
            workflow_ids: [],
            node_types: [],
          },
        },
        selectedWorkflows: {
          ...state.selectedWorkflows,
          [dashboardKey]: [],
        },
        selectedEntities: {
          ...state.selectedEntities,
          [dashboardKey]: [{id: new Date().getTime()}],
        },
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: {
            startDateUTC: null,
            startDate: null,
            endDateUTC: null,
            endDate: null,
          },
        },
      };
    }
    case RECEIVE_NODE_TABLE_COLUMNS: {
      return {...state, nodeTableColumnFilter: action.payload};
    }
    case RECEIVE_PEOPLE_TABLE_COLUMNS: {
      return {...state, peopleTableColumnFilter: action.payload};
    }
    case SET_CURRENT_WORKFLOW_AND_NODE_DATA: {
      return {
        ...state,
        currentWorkflowAndNodeData: {
          ...state.currentWorkflowAndNodeData,
          ...action.payload,
        },
      };
    }
    case SET_CURRENT_DASHBOARD: {
      const {currentDashboard, dashboardKey} = action.payload ?? {};
      const {filters} = currentDashboard ?? {};
      const {workflow_ids = []}: Filters = filters ?? {};
      return {
        ...state,
        currentDashboard: {
          ...state.currentDashboard,
          [dashboardKey]: currentDashboard,
        },
        selectedWorkflows: {
          ...state.selectedWorkflows,
          [dashboardKey]: workflow_ids,
        },
      };
    }

    case RECEIVE_SAVED_DASHBOARDS: {
      const {filters, dashboard} = action.payload;
      return {
        ...state,
        saveDashboardFilterList: {
          ...state.saveDashboardFilterList,
          [dashboard]: filters,
        },
      };
    }
    case SET_LOADING_DASHBOARD_FILTERS: {
      return {
        ...state,
        isDashboardFiltersLoading: action.payload,
      };
    }
    case SET_SELECTED_DASHBOARD_ENTITY_FILTERS: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedDashboardEntityFilters: {
          ...state.selectedDashboardEntityFilters,
          [dashboardKey]: {entities},
        },
      };
    }
    case SET_NAVIGATION_DOWN: {
      return {
        ...state,
        isNavigationDown: action.payload,
      };
    }
    case SET_NAVIGATION_UP: {
      return {
        ...state,
        isNavigationUp: action.payload,
      };
    }
    case RECEIVE_WORKFLOW_TABLE_COLUMNS: {
      return {...state, workflowTableColumnFilter: action.payload};
    }

    case SET_ALL_WORKFLOW_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedAllWorkflowEntities: {
          ...state.selectedAllWorkflowEntities,
          [dashboardKey]: entities,
        },
      };
    }
    case SET_ALL_NODE_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedAllNodeEntities: {
          ...state.selectedAllNodeEntities,
          [dashboardKey]: entities,
        },
      };
    }
    case SET_ALL_PEOPLE_ENTITIES: {
      const {dashboardKey, entities} = action.payload;
      return {
        ...state,
        selectedAllPeopleEntities: {
          ...state.selectedAllPeopleEntities,
          [dashboardKey]: entities,
        },
      };
    }
    case SET_ALL_WORKFLOW_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedAllWorkflowFilters: {
          ...state.selectedAllWorkflowFilters,
          [dashboardKey]: filters,
        },
      };
    }
    case SET_ALL_NODE_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedAllNodeFilters: {
          ...state.selectedAllNodeFilters,
          [dashboardKey]: filters,
        },
      };
    }
    case SET_ALL_PEOPLE_FILTERS: {
      const {dashboardKey, filters} = action.payload;
      return {
        ...state,
        selectedAllPeopleFilters: {
          ...state.selectedAllPeopleFilters,
          [dashboardKey]: filters,
        },
      };
    }
    case SET_SELECTED_DATES: {
      const {dashboardKey, dates} = action.payload;
      return {
        ...state,
        selectedDates: {
          ...state.selectedDates,
          [dashboardKey]: dates,
        },
      };
    }
  }
  return state;
};
